import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { 
  ArrowLeft,
  Phone,
  MapPin,
  Heart,
  Utensils,
  Shield,
  Users,
  Building,
  ExternalLink,
  AlertCircle
} from "lucide-react";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";

const HomelessnessResources = () => {
  const emergencyResources = [
    {
      title: "Philadelphia Homeless Outreach Hotline",
      description: "24/7 hotline for immediate assistance with shelter, food, and emergency services in Philadelphia",
      phone: "215-232-1984",
      icon: <Phone className="w-6 h-6" />,
      color: "#DC2626",
      type: "Emergency Hotline",
      available: "24/7"
    },
    {
      title: "PA 211 - United Way",
      description: "Connect to local resources including shelter, food, utilities assistance, and more",
      phone: "211",
      website: "https://www.pa211.org",
      icon: <Heart className="w-6 h-6" />,
      color: "#DC2626",
      type: "Emergency Hotline",
      available: "24/7"
    }
  ];

  const shelterResources = [
    {
      title: "Office of Homeless Services (OHS)",
      description: "Philadelphia's central resource for emergency shelter placement and homeless services coordination",
      website: "https://www.phila.gov/departments/office-of-homeless-services/",
      address: "1401 JFK Blvd, Philadelphia, PA 19102",
      icon: <Building className="w-6 h-6" />,
      color: "#3F5D4A",
      type: "Government Resource"
    },
    {
      title: "Project HOME",
      description: "Comprehensive services including emergency shelter, permanent supportive housing, healthcare, and employment assistance",
      website: "https://www.projecthome.org",
      phone: "215-232-7272",
      icon: <Shield className="w-6 h-6" />,
      color: "#87A96B",
      type: "Nonprofit Organization"
    },
    {
      title: "Bethesda Project",
      description: "Emergency shelter, transitional housing, and supportive services for individuals experiencing homelessness",
      website: "https://www.bethesdaproject.org",
      phone: "215-985-1600",
      icon: <Users className="w-6 h-6" />,
      color: "#3F5D4A",
      type: "Nonprofit Organization"
    },
    {
      title: "Sunday Breakfast Rescue Mission",
      description: "Emergency shelter, meals, and recovery programs for men experiencing homelessness",
      website: "https://www.sundaybreakfast.org",
      address: "302 N 13th St, Philadelphia, PA 19107",
      phone: "215-922-6400",
      icon: <Utensils className="w-6 h-6" />,
      color: "#87A96B",
      type: "Nonprofit Organization"
    }
  ];

  const supportServices = [
    {
      title: "Benefits Data Trust",
      description: "Free help applying for SNAP, Medicaid, LIHEAP, and other public benefits you may qualify for",
      website: "https://www.bdtrust.org",
      phone: "844-848-4376",
      icon: <Heart className="w-6 h-6" />,
      color: "#3F5D4A"
    },
    {
      title: "Philadelphia Legal Assistance (PLA)",
      description: "Free legal help for low-income individuals facing housing issues, eviction, and benefits denials",
      website: "https://philalegal.org",
      phone: "215-981-3800",
      icon: <Shield className="w-6 h-6" />,
      color: "#87A96B"
    },
    {
      title: "COMPASS PA Benefits Portal",
      description: "Apply online for food stamps (SNAP), cash assistance, healthcare, and other PA benefits",
      website: "https://www.compass.state.pa.us",
      icon: <Building className="w-6 h-6" />,
      color: "#3F5D4A"
    },
    {
      title: "Philadelphia Corporation for Aging (PCA)",
      description: "Resources and support services for seniors 60+ including housing assistance and benefits counseling",
      website: "https://www.pcacares.org",
      phone: "215-765-9040",
      icon: <Users className="w-6 h-6" />,
      color: "#87A96B"
    }
  ];

  const foodResources = [
    {
      title: "Philabundance",
      description: "Find free food distribution sites and pantries throughout Philadelphia",
      website: "https://www.philabundance.org/find-food/",
      phone: "215-339-0900",
      icon: <Utensils className="w-6 h-6" />,
      color: "#87A96B"
    },
    {
      title: "Share Food Program",
      description: "Low-cost food packages and free food distribution sites across the region",
      website: "https://www.sharefoodprogram.org",
      phone: "215-223-2220",
      icon: <Utensils className="w-6 h-6" />,
      color: "#3F5D4A"
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8F4ED]">
      <Header />
      <div className="py-20 px-4">
        <div className="container mx-auto max-w-7xl">
          
          {/* Back Navigation */}
          <div className="mb-8">
            <Link to="/resources" className="inline-flex items-center text-gray-600 hover:text-[#3F5D4A] transition-colors duration-200">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Resource Center
            </Link>
          </div>

          {/* Page Title */}
          <div className="text-center space-y-6 mb-12">
            <div 
            className="inline-flex items-center px-4 py-2 rounded-full text-white text-sm font-semibold"
            style={{backgroundColor: '#DC2626'}}
          >
            IMMEDIATE HELP
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800">
            Homelessness <span style={{color: '#3F5D4A'}}>Resources</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Philadelphia-area resources for emergency shelter, food assistance, and support services. 
            You are not alone — help is available.
          </p>
        </div>

        {/* Emergency Alert Banner */}
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-12">
          <div className="flex items-start space-x-4">
            <AlertCircle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-red-800 text-xl mb-2">Need Immediate Help?</h3>
              <p className="text-red-700 mb-4">
                If you or someone you know is in immediate danger or experiencing a medical emergency, call <strong>911</strong>.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="tel:215-232-1984" className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors">
                  <Phone className="w-4 h-4 mr-2" />
                  Homeless Hotline: 215-232-1984
                </a>
                <a href="tel:211" className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors">
                  <Phone className="w-4 h-4 mr-2" />
                  Dial 211 for Help
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Emergency Hotlines Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <Phone className="w-6 h-6 mr-3" style={{color: '#DC2626'}} />
            Emergency Hotlines
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {emergencyResources.map((resource, index) => (
              <Card key={index} className="border-2 border-red-200 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{backgroundColor: '#FEE2E2'}}
                    >
                      {React.cloneElement(resource.icon, { className: "w-6 h-6 text-red-600" })}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-gray-800 text-lg">{resource.title}</h3>
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-semibold">
                          {resource.available}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-4">{resource.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {resource.phone && (
                          <a 
                            href={`tel:${resource.phone}`}
                            className="inline-flex items-center px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors"
                          >
                            <Phone className="w-4 h-4 mr-2" />
                            Call {resource.phone}
                          </a>
                        )}
                        {resource.website && (
                          <a 
                            href={resource.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Visit Website
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Shelter & Housing Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <Building className="w-6 h-6 mr-3" style={{color: '#3F5D4A'}} />
            Shelter & Housing Assistance
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {shelterResources.map((resource, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{backgroundColor: resource.color + '20'}}
                    >
                      {React.cloneElement(resource.icon, { style: { color: resource.color } })}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-gray-800 text-lg">{resource.title}</h3>
                        <span 
                          className="px-2 py-1 rounded text-xs font-semibold"
                          style={{backgroundColor: resource.color + '20', color: resource.color}}
                        >
                          {resource.type}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-4">{resource.description}</p>
                      {resource.address && (
                        <p className="text-gray-500 text-sm mb-2 flex items-center">
                          <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                          {resource.address}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-4">
                        {resource.phone && (
                          <a 
                            href={`tel:${resource.phone}`}
                            className="inline-flex items-center px-3 py-2 text-white rounded-lg text-sm font-semibold transition-colors"
                            style={{backgroundColor: resource.color}}
                          >
                            <Phone className="w-4 h-4 mr-2" />
                            {resource.phone}
                          </a>
                        )}
                        {resource.website && (
                          <a 
                            href={resource.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Website
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Support Services Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <Heart className="w-6 h-6 mr-3" style={{color: '#87A96B'}} />
            Benefits & Support Services
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {supportServices.map((resource, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{backgroundColor: resource.color + '20'}}
                    >
                      {React.cloneElement(resource.icon, { style: { color: resource.color } })}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-800 text-lg mb-2">{resource.title}</h3>
                      <p className="text-gray-600 text-sm mb-4">{resource.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {resource.phone && (
                          <a 
                            href={`tel:${resource.phone}`}
                            className="inline-flex items-center px-3 py-2 text-white rounded-lg text-sm font-semibold transition-colors"
                            style={{backgroundColor: resource.color}}
                          >
                            <Phone className="w-4 h-4 mr-2" />
                            {resource.phone}
                          </a>
                        )}
                        {resource.website && (
                          <a 
                            href={resource.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Visit Website
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Food Resources Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <Utensils className="w-6 h-6 mr-3" style={{color: '#87A96B'}} />
            Food Assistance
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {foodResources.map((resource, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{backgroundColor: resource.color + '20'}}
                    >
                      {React.cloneElement(resource.icon, { style: { color: resource.color } })}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-800 text-lg mb-2">{resource.title}</h3>
                      <p className="text-gray-600 text-sm mb-4">{resource.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {resource.phone && (
                          <a 
                            href={`tel:${resource.phone}`}
                            className="inline-flex items-center px-3 py-2 text-white rounded-lg text-sm font-semibold transition-colors"
                            style={{backgroundColor: resource.color}}
                          >
                            <Phone className="w-4 h-4 mr-2" />
                            {resource.phone}
                          </a>
                        )}
                        {resource.website && (
                          <a 
                            href={resource.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Find Food Near You
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-[#F0EBE0] border border-[#D4C9B0] rounded-lg p-6 mb-12">
          <h3 className="font-semibold text-[#3F5D4A] mb-2">Important Note</h3>
          <p className="text-[#5C5852] text-sm">
            Resource availability may vary. We recommend calling ahead to confirm services. 
            If you are in immediate danger, please call 911. All resources listed are public services 
            and nonprofit organizations serving the Philadelphia area.
          </p>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div 
            className="rounded-2xl p-8 md:p-12 text-white"
            style={{background: '#3F5D4A'}}
          >
            <h3 className="text-3xl font-bold mb-4">We're Here to Help</h3>
            <p className="text-xl mb-6 opacity-90">
              Dorsey Taylor Homes is committed to supporting families on their path to stable housing. 
              Reach out to learn about our programs and resources.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/programs">
                <Button 
                  className="px-8 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg"
                  style={{
                    background: '#87A96B',
                    borderColor: '#87A96B'
                  }}
                >
                  Explore Our Programs
                </Button>
              </Link>
              <Link to="/">
                <Button 
                  variant="outline"
                  className="border-2 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200"
                  style={{
                    borderColor: '#FEFEFE',
                    color: '#FEFEFE',
                    backgroundColor: 'transparent'
                  }}
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>

        </div>
      </div>
      <Footer />
    </div>
  );
};

export default HomelessnessResources;
